import { motion } from 'framer-motion';
import { Users, Heart, Clock, Star, AlertCircle, TrendingUp, Sparkles } from 'lucide-react';
import { type BranchData, getBranchHealth, FRUIT_VISUAL } from './types';
import { formatDistanceToNow } from 'date-fns';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';

interface RelationshipPanelProps {
  branches: BranchData[];
  totalFruits: number;
  onBranchClick?: (branch: BranchData) => void;
}

const copy = {
  en: {
    title: 'Your Relationship Growth', peopleReached: 'People reached', messagesShared: 'Messages shared',
    strongestBond: 'Strongest bond', lastMessage: 'Last message',
    connectionHealth: 'Connection Health', active: 'active', warm: 'warm', cooling: 'cooling', needCare: 'need care',
    message: 'message', messages: 'messages',
    streak50: '🌟 Legacy Builder — your words are shaping lives',
    streak25: '✨ Deep Connector — your tree is flourishing',
    streak10: '🌿 Growing Strong — keep nurturing your bonds',
    streak5: '🌱 Sprouting — your roots are taking hold',
    streak0: '💫 Just getting started — every message counts',
  },
  es: {
    title: 'Tu Crecimiento Relacional', peopleReached: 'Personas alcanzadas', messagesShared: 'Mensajes compartidos',
    strongestBond: 'Vínculo más fuerte', lastMessage: 'Último mensaje',
    connectionHealth: 'Salud de Conexiones', active: 'activas', warm: 'cálidas', cooling: 'enfriando', needCare: 'necesitan cuidado',
    message: 'mensaje', messages: 'mensajes',
    streak50: '🌟 Constructor de Legado — tus palabras están cambiando vidas',
    streak25: '✨ Conector Profundo — tu árbol está floreciendo',
    streak10: '🌿 Creciendo Fuerte — sigue nutriendo tus lazos',
    streak5: '🌱 Brotando — tus raíces están tomando forma',
    streak0: '💫 Recién empezando — cada mensaje cuenta',
  },
  fr: {
    title: 'Votre Croissance Relationnelle', peopleReached: 'Personnes atteintes', messagesShared: 'Messages partagés',
    strongestBond: 'Lien le plus fort', lastMessage: 'Dernier message',
    connectionHealth: 'Santé des Connexions', active: 'actives', warm: 'tièdes', cooling: 'refroidissent', needCare: 'besoin d\'attention',
    message: 'message', messages: 'messages',
    streak50: '🌟 Bâtisseur d\'héritage — vos mots façonnent des vies',
    streak25: '✨ Connecteur Profond — votre arbre s\'épanouit',
    streak10: '🌿 En pleine croissance — continuez à nourrir vos liens',
    streak5: '🌱 En germination — vos racines prennent forme',
    streak0: '💫 C\'est le début — chaque message compte',
  },
  pt: {
    title: 'Seu Crescimento Relacional', peopleReached: 'Pessoas alcançadas', messagesShared: 'Mensagens compartilhadas',
    strongestBond: 'Vínculo mais forte', lastMessage: 'Última mensagem',
    connectionHealth: 'Saúde das Conexões', active: 'ativas', warm: 'mornas', cooling: 'esfriando', needCare: 'precisam de cuidado',
    message: 'mensagem', messages: 'mensagens',
    streak50: '🌟 Construtor de Legado — suas palavras estão moldando vidas',
    streak25: '✨ Conector Profundo — sua árvore está florescendo',
    streak10: '🌿 Crescendo Forte — continue nutrindo seus laços',
    streak5: '🌱 Brotando — suas raízes estão se firmando',
    streak0: '💫 Recém começando — cada mensagem conta',
  },
  ar: {
    title: 'نمو علاقاتك', peopleReached: 'أشخاص تم الوصول إليهم', messagesShared: 'رسائل مشتركة',
    strongestBond: 'أقوى رابطة', lastMessage: 'آخر رسالة',
    connectionHealth: 'صحة الاتصال', active: 'نشطة', warm: 'دافئة', cooling: 'تبرد', needCare: 'تحتاج رعاية',
    message: 'رسالة', messages: 'رسائل',
    streak50: '🌟 باني إرث — كلماتك تشكل حياة الآخرين',
    streak25: '✨ رابط عميق — شجرتك تزدهر',
    streak10: '🌿 تنمو بقوة — استمر في رعاية روابطك',
    streak5: '🌱 تنبت — جذورك تتشكل',
    streak0: '💫 بداية جديدة — كل رسالة مهمة',
  },
  ja: {
    title: 'あなたの関係の成長', peopleReached: '届いた人数', messagesShared: '共有メッセージ',
    strongestBond: '最も強い絆', lastMessage: '最後のメッセージ',
    connectionHealth: 'つながりの健康度', active: 'アクティブ', warm: '温かい', cooling: '冷めつつ', needCare: 'ケアが必要',
    message: '件', messages: '件',
    streak50: '🌟 レガシービルダー — あなたの言葉が人生を形作っています',
    streak25: '✨ ディープコネクター — あなたの木は花開いています',
    streak10: '🌿 力強く成長中 — 絆を育て続けましょう',
    streak5: '🌱 芽吹き中 — 根が張り始めています',
    streak0: '💫 始まったばかり — すべてのメッセージが大切です',
  },
  ko: {
    title: '당신의 관계 성장', peopleReached: '도달한 사람', messagesShared: '공유한 메시지',
    strongestBond: '가장 강한 유대', lastMessage: '마지막 메시지',
    connectionHealth: '연결 건강도', active: '활발', warm: '따뜻', cooling: '식어가는', needCare: '돌봄 필요',
    message: '개', messages: '개',
    streak50: '🌟 레거시 빌더 — 당신의 말이 삶을 만들어갑니다',
    streak25: '✨ 깊은 연결자 — 나무가 활짝 피고 있어요',
    streak10: '🌿 강하게 성장 중 — 유대를 계속 키우세요',
    streak5: '🌱 싹이 트는 중 — 뿌리를 내리고 있어요',
    streak0: '💫 막 시작했어요 — 모든 메시지가 소중해요',
  },
  id: {
    title: 'Pertumbuhan Hubungan Anda', peopleReached: 'Orang dijangkau', messagesShared: 'Pesan dibagikan',
    strongestBond: 'Ikatan terkuat', lastMessage: 'Pesan terakhir',
    connectionHealth: 'Kesehatan Koneksi', active: 'aktif', warm: 'hangat', cooling: 'mendingin', needCare: 'butuh perhatian',
    message: 'pesan', messages: 'pesan',
    streak50: '🌟 Pembangun Warisan — kata-katamu membentuk kehidupan',
    streak25: '✨ Penghubung Dalam — pohonmu sedang berkembang',
    streak10: '🌿 Tumbuh Kuat — terus rawat ikatanmu',
    streak5: '🌱 Bertunas — akarmu mulai terbentuk',
    streak0: '💫 Baru mulai — setiap pesan berarti',
  },

  hi: {
    title: 'आपके रिश्ते का विकास', peopleReached: 'लोगों तक पहुँचे', messagesShared: 'साझा किए गए संदेश',
    strongestBond: 'सबसे मजबूत बंधन', lastMessage: 'अंतिम संदेश',
    connectionHealth: 'कनेक्शन स्वास्थ्य', active: 'सक्रिय', warm: 'गर्मजोशी भरा', cooling: 'ठंडा पड़ रहा है', needCare: 'देखभाल की जरूरत है',
    message: 'संदेश', messages: 'संदेश',
    streak50: '🌟 लेगेसी बिल्डर — आपके शब्द जीवन को आकार दे रहे हैं',
    streak25: '✨ डीप कनेक्टर — आपका वृक्ष फल-फूल रहा है',
    streak10: '🌿 मज़बूत हो रहा है — अपने रिश्तों को पोषित करते रहें',
    streak5: '🌱 अंकुरित हो रहा है — आपकी जड़ें जम रही हैं',
    streak0: '💫 अभी शुरुआत हुई है — हर संदेश मायने रखता है',
  },
};

const HEALTH_STYLES: Record<string, { bg: string; ring: string }> = {
  active: { bg: 'bg-emerald-500/20', ring: 'ring-emerald-400/50' },
  warm: { bg: 'bg-amber-500/20', ring: 'ring-amber-400/50' },
  cool: { bg: 'bg-blue-500/20', ring: 'ring-blue-400/50' },
  dormant: { bg: 'bg-red-500/20', ring: 'ring-red-400/50' },
};

function getStreakMessage(totalFruits: number, c: typeof copy.en): string {
  if (totalFruits >= 50) return c.streak50;
  if (totalFruits >= 25) return c.streak25;
  if (totalFruits >= 10) return c.streak10;
  if (totalFruits >= 5) return c.streak5;
  return c.streak0;
}

function getHealthSummary(branches: BranchData[]) {
  let active = 0, warm = 0, cool = 0, dormant = 0;
  branches.forEach(b => {
    const h = getBranchHealth(b.lastSentAt);
    if (h === 'active') active++;
    else if (h === 'warm') warm++;
    else if (h === 'cool') cool++;
    else dormant++;
  });
  return { active, warm, cool, dormant };
}

export function RelationshipPanel({ branches, totalFruits, onBranchClick }: RelationshipPanelProps) {
  const { language } = useLanguage();
  const c = lc(copy, language);
  const strongestBranch = branches[0];
  const healthSummary = getHealthSummary(branches);
  const streakMessage = getStreakMessage(totalFruits, c);

  return (
    <motion.div
      className="rounded-2xl p-5 space-y-4 h-full flex flex-col"
      style={{
        background: 'linear-gradient(145deg, rgba(13, 31, 23, 0.95), rgba(10, 24, 16, 0.95))',
        border: '1px solid rgba(255, 215, 100, 0.1)',
        boxShadow: '0 0 30px rgba(0, 0, 0, 0.3)',
      }}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.5, duration: 0.5 }}
    >
      <h3 className="text-sm font-semibold text-amber-300/90 flex items-center gap-2">
        <Star className="h-4 w-4" />
        {c.title}
      </h3>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.04)' }}>
          <p className="text-xs text-white/50">{c.peopleReached}</p>
          <p className="text-lg font-bold text-white">{branches.length}</p>
        </div>
        <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.04)' }}>
          <p className="text-xs text-white/50">{c.messagesShared}</p>
          <p className="text-lg font-bold text-white">{totalFruits}</p>
        </div>
        {strongestBranch && (
          <>
            <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.04)' }}>
              <p className="text-xs text-white/50">{c.strongestBond}</p>
              <p className="text-sm font-semibold text-emerald-300 truncate">{strongestBranch.recipientName}</p>
            </div>
            <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.04)' }}>
              <p className="text-xs text-white/50">{c.lastMessage}</p>
              <p className="text-sm text-white/80">
                {strongestBranch.lastSentAt
                  ? formatDistanceToNow(new Date(strongestBranch.lastSentAt), { addSuffix: true })
                  : '—'}
              </p>
            </div>
          </>
        )}
      </div>

      {/* Branch list */}
      <div 
        className="space-y-2 flex-1 overflow-y-auto min-h-0"
        style={{ maxHeight: '280px' }}
      >
        {branches.map((branch) => {
          const health = getBranchHealth(branch.lastSentAt);
          const style = HEALTH_STYLES[health];
          const primaryTemplate = branch.fruits[0]?.template || 'custom';
          const visual = FRUIT_VISUAL[primaryTemplate];

          return (
            <button
              key={branch.recipientName}
              className={`w-full flex items-center gap-3 p-2.5 rounded-xl ring-1 ${style.ring} ${style.bg} hover:brightness-125 transition-all text-left`}
              onClick={() => onBranchClick?.(branch)}
            >
              <span className="text-lg">{visual.emoji}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{branch.recipientName}</p>
                <p className="text-xs text-white/50">
                  {branch.fruits.length} {branch.fruits.length !== 1 ? c.messages : c.message}
                  {branch.lastSentAt && (
                    <> · {formatDistanceToNow(new Date(branch.lastSentAt), { addSuffix: true })}</>
                  )}
                </p>
              </div>
              {health === 'dormant' && (
                <AlertCircle className="h-4 w-4 text-red-400/70 flex-shrink-0" />
              )}
            </button>
          );
        })}
      </div>

      {/* Connection Health Summary */}
      <div className="rounded-xl p-3 space-y-2" style={{ background: 'rgba(255,255,255,0.04)' }}>
        <p className="text-xs font-medium text-white/60 flex items-center gap-1.5">
          <TrendingUp className="h-3 w-3" />
          {c.connectionHealth}
        </p>
        <div className="flex gap-2 flex-wrap">
          {healthSummary.active > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">
              {healthSummary.active} {c.active}
            </span>
          )}
          {healthSummary.warm > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300">
              {healthSummary.warm} {c.warm}
            </span>
          )}
          {healthSummary.cool > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300">
              {healthSummary.cool} {c.cooling}
            </span>
          )}
          {healthSummary.dormant > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-300">
              {healthSummary.dormant} {c.needCare}
            </span>
          )}
        </div>
      </div>

      {/* Growth milestone */}
      <div className="rounded-xl p-3" style={{ background: 'rgba(255, 215, 100, 0.05)', border: '1px solid rgba(255, 215, 100, 0.1)' }}>
        <p className="text-xs text-amber-200/80 flex items-center gap-1.5">
          <Sparkles className="h-3 w-3 text-amber-400" />
          {streakMessage}
        </p>
      </div>
    </motion.div>
  );
}
